import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ViewUserProfileResponse } from '../models/user.model';
import { TemperatureUnitService } from '../../dashboard/services/temperature-unit.service';

export interface MenuItem {
  id: string;
  label: string;
  route: string;
  icon?: string;
  tooltip: string;
  cssClass: string;
  allowedRoles: string[];
  requiredClaims?: string[];
  hidden?: boolean;
}

export enum UserRole {
  SUPER_ADMIN = 'Super Admin',
  CLIENT_ADMIN = 'Client Admin',
  CUSTOMER_ADMIN = 'Customer Admin',
  INSTALLER = 'Installer',
  RETAIL_ADMIN = 'Retail Admin',
  RETAIL_MANAGER = 'Retail Manager',
  RETAIL_USER = 'Retail User',
  RETAIL_USER_WITH_CONTROL = 'Retail User With Control',
  USER = 'User'
}

@Injectable({
  providedIn: 'root'
})
export class UserProfileService {

  /**
   * Helper function for case-insensitive role matching with backend enum values
   */
  private roleMatches(backendRole: string, enumRole: UserRole): boolean {
    return backendRole?.toLowerCase().trim() === enumRole.toLowerCase().trim();
  }
  private userProfileSubject = new BehaviorSubject<ViewUserProfileResponse | null>(null);
  public userProfile$ = this.userProfileSubject.asObservable();

  // // Centralized menu configuration because of 
  // - Easy to add new menus: Just add to the configuration array
  // - Role changes: Update roles in one place
  // - Claim-based permissions: Optional granular control per menu item
  // - Consistent structure: All menu properties centralized
  private readonly menuItems: MenuItem[] = [
    {
      id: 'clients',
      label: 'Clients',
      route: '/clients',
      tooltip: 'Clients',
      cssClass: 'header-links clients_menu',
      allowedRoles: [UserRole.SUPER_ADMIN]
    },
    {
      id: 'brands',
      label: 'Brands',
      route: '/brands',
      tooltip: 'Brands',
      cssClass: 'header-links brands_menu',
      allowedRoles: [UserRole.SUPER_ADMIN]
    },
    {
      id: 'customers',
      label: 'Customers',
      route: '/customers',
      tooltip: 'Customers',
      cssClass: 'header-links customers_menu',
      allowedRoles: [
        UserRole.CLIENT_ADMIN,
        UserRole.CUSTOMER_ADMIN,
        UserRole.USER
      ]
    },
    {
      id: 'locations',
      label: 'Locations',
      route: '/locations',
      tooltip: 'Locations',
      cssClass: 'header-links locations_menu',
      allowedRoles: [
        UserRole.RETAIL_ADMIN,
        UserRole.RETAIL_MANAGER,
        UserRole.RETAIL_USER_WITH_CONTROL,
        UserRole.RETAIL_USER
      ]
    },
    {
      id: 'devicetemplate',
      label: 'Device Template',
      route: '/devicetemplate',
      tooltip: 'Device Template',
      cssClass: 'header-links devicetemplate_menu',
      allowedRoles: [UserRole.SUPER_ADMIN]
    },
    {
      id: 'devices',
      label: 'Devices',
      route: '/devices',
      tooltip: 'Devices',
      cssClass: 'header-links devices_menu',
      allowedRoles: [UserRole.SUPER_ADMIN, UserRole.CLIENT_ADMIN, UserRole.CUSTOMER_ADMIN, UserRole.INSTALLER]
    },
    // {
    //   id: 'management',
    //   label: 'Management',
    //   route: '/management',
    //   tooltip: 'Management',
    //   cssClass: 'header-links management_menu',
    //   allowedRoles: [UserRole.SUPER_ADMIN, UserRole.CLIENT_ADMIN, UserRole.CUSTOMER_ADMIN, UserRole.INSTALLER]
    // },
    {
      id: 'firmware',
      label: 'Firmware',
      route: '/firmware',
      tooltip: 'Firmware',
      cssClass: 'header-links firmware_menu',
      allowedRoles: [UserRole.SUPER_ADMIN, UserRole.CLIENT_ADMIN, UserRole.INSTALLER]
    },
    {
      id: 'reports',
      label: 'Reports',
      route: '/reports',
      tooltip: 'Reports',
      cssClass: 'header-links reports_menu',
      allowedRoles: [UserRole.SUPER_ADMIN, UserRole.CLIENT_ADMIN]
    },
    {
      id: 'usermanagement',
      label: 'Manage User',
      route: '/usermanagement',
      tooltip: 'Manage User',
      cssClass: 'header-links manage_user_menu',
      allowedRoles: [UserRole.SUPER_ADMIN, UserRole.CLIENT_ADMIN, UserRole.CUSTOMER_ADMIN, UserRole.RETAIL_ADMIN, UserRole.RETAIL_MANAGER]
      // requiredClaims: ['user.read'] // Optional claim requirement
    },
    {
      id: 'scheduler',
      label: 'Scheduler',
      route: '/scheduler',
      tooltip: 'Scheduler',
      cssClass: 'header-links scheduler_menu',
      allowedRoles: [UserRole.SUPER_ADMIN, UserRole.CLIENT_ADMIN, UserRole.CUSTOMER_ADMIN, UserRole.RETAIL_ADMIN, UserRole.RETAIL_MANAGER, UserRole.RETAIL_USER, UserRole.RETAIL_USER_WITH_CONTROL]
    },
    {
      id: 'alerts',
      label: 'Alerts',
      route: '/alerts',
      tooltip: 'Alerts',
      cssClass: 'header-links alert_menu',
      allowedRoles: [UserRole.SUPER_ADMIN, UserRole.CLIENT_ADMIN, UserRole.CUSTOMER_ADMIN, UserRole.RETAIL_ADMIN, UserRole.RETAIL_MANAGER, UserRole.RETAIL_USER_WITH_CONTROL, UserRole.RETAIL_USER, UserRole.USER]
    },
    {
      id: 'rules',
      label: 'Rules',
      route: '/rules',
      tooltip: 'Rules',
      cssClass: 'header-links rules_menu',
      allowedRoles: [UserRole.SUPER_ADMIN, UserRole.CLIENT_ADMIN, UserRole.CUSTOMER_ADMIN, UserRole.RETAIL_MANAGER, UserRole.RETAIL_ADMIN]
    },
    {
      id: 'jitprules',
      label: 'JITP Rules',
      route: '/jitprules',
      tooltip: 'JITP Rules',
      cssClass: 'header-links jitprules_menu',
      allowedRoles: [UserRole.SUPER_ADMIN, UserRole.CLIENT_ADMIN],
      hidden: true
    },
  ];

  constructor(private temperatureUnitService: TemperatureUnitService) {
    this.loadUserProfileFromStorage();
  }

  /**
   * Load user profile from localStorage
   */
  loadUserProfileFromStorage(): void {
    const userProfileData = localStorage.getItem('userProfile');
    if (userProfileData) {
      const userProfile: ViewUserProfileResponse = JSON.parse(userProfileData);
      this.userProfileSubject.next(userProfile);
      if (userProfile.temperatureUnit) {
        this.temperatureUnitService.setUnitFromProfileValue(userProfile.temperatureUnit);
      }
    }
  }

  /**
   * Set user profile and update localStorage
   * @param userProfile - User profile data
   */
  setUserProfile(userProfile: ViewUserProfileResponse): void {
    localStorage.setItem('userProfile', JSON.stringify(userProfile));
    this.userProfileSubject.next(userProfile);
    if (userProfile.temperatureUnit) {
      this.temperatureUnitService.setUnitFromProfileValue(userProfile.temperatureUnit);
    }
  }

  /**
   * Get current user profile synchronously
   */
  getCurrentUserProfile(): ViewUserProfileResponse | null {
    return this.userProfileSubject.value;
  }

  /**
   * Check if user has a specific claim
   * @param claimCode - The claim code to check
   */
  hasClaim(claimCode: string): boolean {
    const userProfile = this.getCurrentUserProfile();
    if (!userProfile || !userProfile.claimList) {
      return false;
    }
    return userProfile.claimList.includes(claimCode);
  }

  /**
   * Get all visible menu items for the current user
   */
  getVisibleMenuItems(): MenuItem[] {
    const userProfile = this.getCurrentUserProfile();
    if (!userProfile || !userProfile.roleName) {
      return [];
    }

    const userRole = userProfile.roleName.toLowerCase().trim();
    let filteredMenuItems = this.menuItems;

    if (!userProfile.isMenuVisible) {
      // remove scheduler menu item if isMenuVisible is false for user
      filteredMenuItems = filteredMenuItems.filter(item => item.id !== "scheduler");
    }

    return filteredMenuItems.filter(menuItem => {
      // Skip hidden menu items (accessible via route but not shown in sidebar)
      if (menuItem.hidden) {
        return false;
      }

      // Check role permission using case-insensitive matching
      const hasRoleAccess = menuItem.allowedRoles.some(allowedRole => {
        return allowedRole.toLowerCase().trim() === userRole;
      });

      if (!hasRoleAccess) {
        return false;
      }

      // Check claim permission if required
      if (menuItem.requiredClaims && menuItem.requiredClaims.length > 0) {
        return menuItem.requiredClaims.some(claim => this.hasClaim(claim));
      }

      return true;
    });
  }

  /**
   * Check if a specific menu item is visible to the current user
   */
  isMenuItemVisible(menuId: string): boolean {
    const visibleItems = this.getVisibleMenuItems();
    return visibleItems.some(item => item.id === menuId);
  }

  /**
   * Check if user should see Clients menu item (backward compatibility)
   */
  canViewClients(): boolean {
    return this.isMenuItemVisible('clients');
  }

  /**
   * Check if user should see Customers menu item (backward compatibility)
   */
  canViewCustomers(): boolean {
    return this.isMenuItemVisible('customers');
  }

  /**
   * Get user's role name
   */
  getUserRole(): string | null {
    const userProfile = this.getCurrentUserProfile();
    return userProfile?.roleName || null;
  }

  /**
   * Get user's display name
   */
  getDisplayName(): string | null {
    const userProfile = this.getCurrentUserProfile();
    return userProfile?.displayName || null;
  }

  /**
   * Get user's client ID based on role
   * Super Admin: returns null (unrestricted access)
   * All other roles: returns clientId from user profile
   */
  getClientId(): string | null {
    const userProfile = this.getCurrentUserProfile();
    if (!userProfile) {
      return null;
    }

    // Super Admin has unrestricted access (no clientId filter)
    if (this.roleMatches(userProfile.roleName, UserRole.SUPER_ADMIN)) {
      return null;
    }

    // All other roles use their clientId from the Users table
    return userProfile.clientId || null;
  }

  /**
   * Check if current user is Super Admin (has unrestricted access)
   */
  isSuperAdmin(): boolean {
    const userProfile = this.getCurrentUserProfile();
    if (!userProfile) {
      return false;
    }
    return this.roleMatches(userProfile.roleName, UserRole.SUPER_ADMIN);
  }

  /**
   * Clear user profile data
   */
  clearUserProfile(): void {
    localStorage.removeItem('userProfile');
    this.userProfileSubject.next(null);
  }

  /**
   * Check if user has access to a specific route based on their role
   */
  hasRouteAccess(route: string): boolean {
    const userProfile = this.getCurrentUserProfile();
    if (!userProfile || !userProfile.roleName) {
      return false;
    }

    const userRole = userProfile.roleName.toLowerCase().trim();
    const menuItem = this.menuItems.find(item => item.route === route);

    if (!menuItem) {
      return false;
    }

    // Check role permission using case-insensitive matching
    const hasRoleAccess = menuItem.allowedRoles.some(allowedRole =>
      allowedRole.toLowerCase().trim() === userRole
    );

    if (!hasRoleAccess) {
      return false;
    }

    // Check claim permission if required
    if (menuItem.requiredClaims && menuItem.requiredClaims.length > 0) {
      return menuItem.requiredClaims.some(claim => this.hasClaim(claim));
    }

    return true;
  }
}